import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha' // eslint-disable-line import/no-named-as-default
import { connect } from 'react-redux'
import { renderMarkup } from 'react-render-markup'
import { useLocation, useNavigate } from 'react-router'
import { createSelector, Dispatch } from '@reduxjs/toolkit'

import {
  addNotification,
  removeNotification as removeNotificationAction
} from '../notifications/notifications-actions'
import type { NotificationType } from '../notifications/types'
import { killState as killStateAction } from '../rootReducer'
import { getReCaptchaKey, photoCaptureHelp, supplementRequestsHelp } from '../shared/constants'
import { WrappedTdsModal } from '../shared/TdsModal'
import type { GlobalState } from '../types'
import { claimError, enableInputs, fetchClaim as fetchClaimAction } from './claim-actions'
import { MAINTENANCE_MESSAGE } from './claim-constants'
import { ClaimDisplay } from './ClaimDisplay'
import { ClaimForm } from './ClaimForm'
import type { ClaimState, FetchClaimArgs } from './types'

type ClaimProps = {
  addNotification(arg: NotificationType): void
  notifications: NotificationType[]
  enableInputs(): void
  removeNotification(arg: string): void
  fetchClaim({ claimNumber, dateOfLoss, recaptchaToken }: FetchClaimArgs): void
  killState(): void
  formValues: {
    values?: {
      claimNumber?: string
      dateOfLoss?: string
      roleInClaim?: string
    }
  }
  claim: ClaimState
  disabled: boolean
}


const validClaimNumber = (claimNumber: string) => /^[A-Z0-9]{7}$/.test(claimNumber)
const validDateOfLoss = (dateOfLoss: string) => /^\d{4}-\d{2}-\d{2}$/.test(dateOfLoss)

const ClaimComponent = (props: ClaimProps) => {
  const {
    addNotification,
    disabled,
    enableInputs,
    fetchClaim,
    formValues,
    notifications,
    removeNotification,
    claim: claimState,
    killState
  } = props

  const [medicalNotification, setMedicalNotification] = useState<boolean>(false)
  const [disableContinue, setDisableContinue] = useState<boolean>(false)
  const [repairerNotification, setRepairerNotification] = useState<boolean>(false)
  const [policyHolderClaimantNotification, setPolicyHolderClaimantNotification] =
    useState<boolean>(false)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [role, setRole] = useState<string>('')

  const refRecaptcha = useRef<ReCAPTCHA>(null)
  const refIsFirstRun = useRef(true)
  const navigate = useNavigate()
  const location = useLocation()

  const formIncomplete = useMemo<boolean>(() => {
    return !formValues?.values?.claimNumber
      || !formValues?.values?.dateOfLoss
      || !formValues.values.roleInClaim
  }, [formValues])

  useEffect(() => {
      // For prefill (disabled === true), we do the ReCAPTCHA on clicking the submit button
      // Here we watch the claimState for changes and if prefill comes back valid, we navigate to
      // the identity page.
      if (disabled && !claimState.requesting && claimState.valid) {
        navigate(`${window.travURI}identity`)
      }
  }, [claimState, disabled, navigate])

  useEffect(() => {
    // Reset LOB additional attribute
    document.title ='File Uploads - Find Claim'

    if (location && location.state && location.state.claimError) {
      // Session timeout path
      // clear location state to prevent this block from running on refresh.
      window.history.replaceState({}, document.title)

      // Kill State for previously entered information
      killState()
      addNotification({
        name: 'claimError',
        body: location.state.claimError,
        title: 'SECURE SESSION EXPIRED',
        severity: 'warning'
      })
    }
  }, [location, addNotification, killState])

  useEffect(() => {
    if (refRecaptcha?.current && !disabled && !claimState?.requesting) {
      if (!refIsFirstRun.current) {
        refRecaptcha.current.reset()
      } else {
        refIsFirstRun.current = false
      }
    }
  }, [disabled, claimState?.requesting])


  useEffect(() => {
    return () => {
      enableInputs()
    }
  }, [enableInputs])

  const sendRequest = useCallback((recaptchaToken: string) => {
    fetchClaim({
      claimNumber: formValues.values?.claimNumber ?? '',
      dateOfLoss: formValues.values?.dateOfLoss ?? '',
      recaptchaToken
    })
  }, [fetchClaim, formValues])

  // Redux Form will only call method once all form values are valid
  const submit = useCallback((event: React.SyntheticEvent) => {
    // prevent form from making query that refreshes page and prevents
    // recaptcha and saga calls.
    event.preventDefault()
    if (notifications.length > 0) {
      removeNotification('claimError')
    }
    // Watch the claim state for changes. If a valid claim is found, navigate to the identity page.
    // If there are errors, check for maintenance message and navigate to maintenance page.
    if (claimState.errors && claimState.errors.length > 0) {
      const maintenanceError =
        claimState.errors.some((error: any) => error?.message === MAINTENANCE_MESSAGE)
      if (maintenanceError) {
        navigate(`${window.travURI}maintenance`)
        return
      }
    }
    if (claimState.valid && claimState.requesting === false) {
      navigate(`${window.travURI}identity`)
      return
    }
    if (disabled) {
      refRecaptcha?.current?.execute()
    }
  }, [notifications, removeNotification, claimState, navigate, refRecaptcha, disabled])

  useEffect(() => {
    const claimNumber = formValues?.values?.claimNumber
    const dateOfLoss = formValues?.values?.dateOfLoss
    if (!claimNumber || !dateOfLoss) return
    if (!validClaimNumber(claimNumber) || !validDateOfLoss(dateOfLoss)) return
    if (isFocused) return // If editing claim or date, do not yet execute ReCAPTCHA
    // Execute ReCAPTCHA once claim number and date of loss are valid to gather info

    refRecaptcha?.current?.execute()
  }, [formValues?.values?.claimNumber, formValues?.values?.dateOfLoss, isFocused])

  const roleSelect = useCallback((event: FormEvent<HTMLSelectElement>) => {
    setRole(event.currentTarget.value)
  }, [])

  // Logic to display modals whenever the user changes role or gets a new claim
  useEffect(() => {
    if (claimState?.claimDetails?.lob?.toUpperCase() === 'A') {
      switch (role) {
        case 'medicalHealth':
          setMedicalNotification(true)
          setDisableContinue(true)
          break
        case 'repairCont':
          setRepairerNotification(true)
          setDisableContinue(false)
          break
        case 'travelersPolicyHolder':
          setPolicyHolderClaimantNotification(true)
          setDisableContinue(false)
          break
        case 'claimant':
          setPolicyHolderClaimantNotification(true)
          setDisableContinue(false)
          break
        default:
          setPolicyHolderClaimantNotification(false)
          setRepairerNotification(false)
          setMedicalNotification(false)
          setDisableContinue(false)
      }
    }
  }, [claimState?.claimDetails?.lob, role])

  const onRecaptchaResolved = useCallback(() => {
    const recaptchaToken = refRecaptcha?.current?.getValue()
    if (recaptchaToken) {
      sendRequest(recaptchaToken)
    } else {
      addNotification({
        name: 'claimError',
        body: 'We are unable to process your request. Please contact your Claim professional.',
        severity: 'error',
        title: 'Claim Error'
      })
    }
  }, [addNotification, sendRequest])

  const captchaKey = getReCaptchaKey(window.location.hostname)

  // format to reflect MM/DD/YYYY format
  const formattedDateOfLost = useMemo<string>(() => {
    return !claimState?.claimDetails?.dateOfLoss
    ? ''
    : claimState?.claimDetails?.dateOfLoss.slice(0,2) +
      '/' + claimState?.claimDetails?.dateOfLoss.slice(2,4) + '/' +
      claimState?.claimDetails?.dateOfLoss.slice(-4)
  }, [claimState?.claimDetails?.dateOfLoss])

  const medicalHelp = useMemo<string>(() =>
    // eslint-disable-next-line max-len
    `<p>Medical providers should not use this process.  Use eBill or, for more information on how to submit medical bills or reports for consideration under Auto Medical Payments or Personal Injury Protection coverages, please contact the Provider Inquiry Center at 877-838-7281 or <a href="mailto:providerinquiry@travelers.com?subject=${'Claim Number ' + claimState?.claimDetails?.claim + ' – DOL ' + formattedDateOfLost + ' – Upload Center Inquiry'}">providerinquiry@travelers.com</a>.</p>`,
    [claimState?.claimDetails?.claim, formattedDateOfLost]
  )

  const isAuto = useMemo<boolean>(() => {
    return claimState?.claimDetails?.lob?.toUpperCase() === 'A'
  }, [claimState?.claimDetails?.lob])

  return (
    <ClaimDisplay>
      <ClaimForm
        handleSubmit={submit}
        requesting={claimState.requesting}
        disabled={disabled}
        disableContinue={disableContinue}
        roleSelect={roleSelect}
        setIsFocused={setIsFocused}
        formIncomplete={formIncomplete}
        roleDisabled={!claimState?.valid} // Disable role selection until claim is valid
        isAuto={isAuto}
      />
      {
        medicalNotification
          ? <WrappedTdsModal
            label='Medical Provider Disable'
            size='medium'
            buttonText='Okay'
            onTdsClose={() => setMedicalNotification(false)}
            hasCancelButton={false}
          >
            {renderMarkup(medicalHelp)}
          </WrappedTdsModal>
          : null
      }
      {
        policyHolderClaimantNotification
          ? <WrappedTdsModal
            label='Policyholder/Claimant Notification'
            size='medium'
            buttonText='Okay'
            onTdsClose={() => setPolicyHolderClaimantNotification(false) }
            modalTitle='Photo capture'
            hasCancelButton={false}
          >
            {renderMarkup(photoCaptureHelp)}
          </WrappedTdsModal>
          : null
      }
      {
        repairerNotification
          ? <WrappedTdsModal
            onTdsClose={() => setRepairerNotification(false) }
            label='Policyholder/Claimant Notification'
            size='medium'
            buttonText='Go To Supplement Portal'
            handleOk={() =>
              window.open('https://supplements.travelers.com/', '_self')
            }
            modalTitle='Supplement requests'
            hasCancelButton={false}
          >
            {renderMarkup(supplementRequestsHelp)}
          </WrappedTdsModal>
          : null
      }
      <ReCAPTCHA
        id='claimFormRecaptcha'
        size='invisible'
        ref={refRecaptcha}
        sitekey={captchaKey}
        onChange={onRecaptchaResolved}
      />
    </ClaimDisplay>
  )
}

const notificationSelector = (state: GlobalState) => state.notifications.notifications

const getNotifications = createSelector(
  notificationSelector,
  (notifications: NotificationType[]) =>
    notifications.filter((n: NotificationType) => n.name === 'claimError')
)

const mapStateToProps = (state: GlobalState) => ({
  claim: state.claim,
  formValues: state.form.claimForm,
  disabled: state.claim.disableInputs,
  notifications: getNotifications(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchClaim: ({ claimNumber, dateOfLoss, recaptchaToken }: FetchClaimArgs) =>
    dispatch(fetchClaimAction({ claimNumber, dateOfLoss, recaptchaToken })),
  killState: () => dispatch(killStateAction()),
  claimError: (error: unknown) => dispatch(claimError(error)),
  enableInputs: () => dispatch(enableInputs()),
  removeNotification: (name: string) => dispatch(removeNotificationAction(name)),
  addNotification: (options: NotificationType) => dispatch(addNotification(options))
})

const Claim = connect(mapStateToProps, mapDispatchToProps)(ClaimComponent)

export {
  Claim,
  ClaimComponent // for testing without HOC
}
